import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { profileAssumptionService } from "../../utils/services/profileAssumptionService";
import { demandProfileService } from "../../utils/services/demandProfileService";
import { inputFieldSx, selectFieldSx } from "../../constants/fieldStyles";

const ProfileAssumptionModal = ({ open, onClose, onSuccess, editData }) => {
  const isEdit = !!editData;

  const initialData = {
    profile: "",
    safetyStockWeeks: "",
    lossRate: "",
  };

  const [formData, setFormData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [demandProfiles, setDemandProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        profile: editData.profile?._id || editData.profile || "",
        safetyStockWeeks: editData.safetyStockWeeks || "",
        lossRate: editData.lossRate || "",
      });
    } else {
      setFormData(initialData);
    }
  }, [editData, open]);

  useEffect(() => {
    if (open) {
      fetchDemandProfiles();
    }
  }, [open]);

  const fetchDemandProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const response = await demandProfileService.fetch();
      setDemandProfiles(response.data || []);
    } catch (error) {
      console.error("Failed to fetch demand profiles:", error);
      toast.error("Failed to load demand profiles");
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.profile) {
      toast.error("Profile is required");
      return;
    }
    if (
      !formData.safetyStockWeeks ||
      isNaN(formData.safetyStockWeeks) ||
      formData.safetyStockWeeks <= 0
    ) {
      toast.error("Safety stock weeks must be a valid positive number");
      return;
    }
    if (
      !formData.lossRate ||
      isNaN(formData.lossRate) ||
      formData.lossRate < 0 ||
      formData.lossRate > 100
    ) {
      toast.error("Loss rate must be a valid percentage between 0 and 100");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        profile: formData.profile,
        safetyStockWeeks: Number(formData.safetyStockWeeks),
        lossRate: Number(formData.lossRate),
      };

      if (isEdit) {
        await profileAssumptionService.edit(submitData, editData._id);
        toast.success("Profile assumption updated successfully!");
      } else {
        await profileAssumptionService.create(submitData);
        toast.success("Profile assumption created successfully!");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Submit failed:", error);
      toast.error("Failed to save profile assumption");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialData);
    setDemandProfiles([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Profile Assumption" : "Create Profile Assumption"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth required>
            <InputLabel>Profile</InputLabel>
            <Select
              value={formData.profile}
              label="Profile"
              onChange={(e) => handleInputChange("profile", e.target.value)}
              disabled={loadingProfiles}
              sx={selectFieldSx}
            >
              {demandProfiles.map((profile) => (
                <MenuItem key={profile._id} value={profile._id}>
                  {profile.item}
                </MenuItem>
              ))}
            </Select>
            {loadingProfiles && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </FormControl>
          <TextField
            fullWidth
            label="Safety Stock Weeks"
            type="number"
            value={formData.safetyStockWeeks}
            onChange={(e) =>
              handleInputChange("safetyStockWeeks", e.target.value)
            }
            required
            inputProps={{ min: 0, step: 0.1 }}
            sx={inputFieldSx}
          />
          <TextField
            fullWidth
            label="Loss Rate (%)"
            type="number"
            value={formData.lossRate}
            onChange={(e) => handleInputChange("lossRate", e.target.value)}
            required
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            sx={inputFieldSx}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? (
            <CircularProgress size={20} />
          ) : isEdit ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileAssumptionModal;
